import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getCustomAttributesTypes } from 'lib/customAttributes';
import {
  Badge,
  EmptyIndicator,
  LoadingIndicator,
  MoreOptions
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  ProductUserCustomAttributesAdd,
  ProductUserCustomAttributesChangeOrder,
  ProductUserCustomAttributesDelete,
  ProductUserCustomAttributesEdit
} from '.';

import type { GetProductUserCustomAttributesRes } from 'shared-types/responses';

interface Props {
  hideAnalyticsControl: boolean;
  productId: number;
  title?: string;
  titleInfo?: string;
  type: 'achievement' | 'calendar-event';
  useFieldTranslation?: boolean;
}

const ProductUserCustomAttributes = ({
  hideAnalyticsControl,
  productId,
  title = '',
  titleInfo,
  type,
  useFieldTranslation
}: Props) => {
  const { language, messages, theme, user } = useReduxState();

  const { data, loading, updateData } =
    useApiData<GetProductUserCustomAttributesRes>({
      url: `/admin/product-user-custom-attributes/${type}/${productId}`
    });

  const attributeTypes = getCustomAttributesTypes({ messages });

  const userIsSuperadmin = user?.roles.includes('superadmin');

  return loading ? (
    <LoadingIndicator />
  ) : (
    <div className="custom-attributes">
      <Box
        headerContent={
          <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
            <ProductUserCustomAttributesAdd
              addAttributeSuccessText={
                useFieldTranslation
                  ? messages.commonAddFieldSuccess
                  : messages.attributesAddSuccess
              }
              addAttributeText={
                useFieldTranslation
                  ? messages.commonAddField
                  : messages.attributesAdd
              }
              hideAnalyticsControl={hideAnalyticsControl}
              numberOfAttributes={data?.customAttributes?.length || 0}
              updateData={updateData}
              urlState={`${type}/${productId}`}
            />

            <MoreOptions category="lighter">
              <ProductUserCustomAttributesChangeOrder
                attributes={data?.customAttributes}
                updateData={updateData}
                urlState={`${type}/${productId}`}
              />

              <ProductUserCustomAttributesDelete
                deleteAllDescription={
                  useFieldTranslation
                    ? messages.commonDeleteAllFieldsConfirm
                    : messages.attributesDeleteConfirmAll
                }
                deleteAllSuccessText={messages.commonDeleteFieldSuccessAll}
                updateData={updateData}
                urlState={`${type}/${productId}`}
              />
            </MoreOptions>
          </div>
        }
        headerContentWrap={true}
        padding={0}
        title={title || messages.usersCustomAttributes}
        titleDescription={titleInfo}
      >
        {data?.customAttributes?.length ? (
          <div style={{ overflowX: 'auto' }}>
            <BoxTable>
              <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
                <BoxTableCell>{messages.commonTitle}</BoxTableCell>
                <BoxTableCell>{messages.commonType}</BoxTableCell>
                <BoxTableCell>{messages.commonRequired}</BoxTableCell>
                {userIsSuperadmin && !hideAnalyticsControl ? (
                  <BoxTableCell>
                    {messages.commonIncludeInAnalytics}
                  </BoxTableCell>
                ) : null}
                <BoxTableCell></BoxTableCell>
              </BoxTableHeader>

              {data.customAttributes.map((attribute, i) => (
                <BoxTableRow key={i}>
                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    {attribute.titleTranslations?.[language] || attribute.title}
                  </BoxTableCell>

                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    <div className="nowrap">
                      {attributeTypes[attribute.type]}

                      {attribute.options?.length ? (
                        <div className="count">{attribute.options.length}</div>
                      ) : null}
                    </div>
                  </BoxTableCell>

                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    <div className="nowrap">
                      {attribute.isRequired ? (
                        <Badge
                          category="dark"
                          size="small"
                          text={messages.commonYes}
                        />
                      ) : null}
                    </div>
                  </BoxTableCell>

                  {userIsSuperadmin && !hideAnalyticsControl ? (
                    <BoxTableCell cellBreakpoint={breakpoints.medium}>
                      <div className="nowrap">
                        {attribute.includeInAnalytics ? (
                          <Badge
                            category="dark"
                            size="small"
                            text={messages.commonYes}
                          />
                        ) : null}
                      </div>
                    </BoxTableCell>
                  ) : null}

                  <BoxTableCell shrink={true}>
                    {attribute.editableBy === 'admin' ||
                    (attribute.editableBy === 'superadmin' &&
                      user?.roles.includes('superadmin')) ||
                    (attribute.editableBy === 'vuoadmin' &&
                      user?.roles.includes('vuoadmin')) ? (
                      <MoreOptions
                        styleContentButtons={false}
                        renderAsFixed={true}
                      >
                        <ProductUserCustomAttributesEdit
                          attribute={attribute}
                          editAttributeSuccessText={
                            useFieldTranslation
                              ? messages.commonChangesSavesSuccessfully
                              : messages.attributesEditSuccess
                          }
                          editAttributeText={
                            useFieldTranslation
                              ? messages.commonEditField
                              : messages.attributesEdit
                          }
                          hideAnalyticsControl={hideAnalyticsControl}
                          updateData={updateData}
                          urlState={`${type}/${productId}`}
                        />

                        <ProductUserCustomAttributesDelete
                          deleteDescription={
                            useFieldTranslation
                              ? messages.commonDeleteFieldConfirm
                              : messages.attributesDeleteConfirm
                          }
                          deleteSuccessText={messages.commonDeleteFieldSuccess}
                          id={attribute.id}
                          updateData={updateData}
                          urlState={`${type}/${productId}`}
                        />
                      </MoreOptions>
                    ) : null}
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </div>
        ) : (
          <EmptyIndicator
            icon="tag"
            title={
              useFieldTranslation
                ? messages.commonNoFields
                : messages.attributesEmpty
            }
          />
        )}
      </Box>

      <style jsx>{`
        .custom-attributes {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }
        .nowrap {
          white-space: nowrap;
        }
        .count {
          background-color: ${theme.lightGreyDark};
          border-radius: 10px;
          color: #555;
          display: inline-block;
          font-size: 1.3rem;
          font-weight: 700;
          line-height: 1;
          margin-left: 5px;
          padding: 3px 6px;
          position: relative;
          top: -1px;
        }
      `}</style>
    </div>
  );
};

export default ProductUserCustomAttributes;
